<template>
  <div
    class="onboarding-questionnaire onboarding-questionnaire--default"
    :class="{
      'onboarding-questionnaire--summary': isSummaryPage,
      'onboarding-questionnaire--personalize': isPersonalizePage,
    }"
  >
    <Transition name="header-gradient">
      <div
        v-if="showStepperGradient"
        class="onboarding-questionnaire__header-gradient"
        aria-hidden="true"
      >
        <img
          class="onboarding-questionnaire__header-gradient-image"
          src="@/assets.app/images/onboarding/stepper-gradient.svg"
          alt=""
        />
      </div>
    </Transition>

    <Transition name="fade" mode="out-in">
      <div
        v-if="isQuestionnaireHeaderVisible"
        key="stepper"
        class="onboarding-questionnaire__header"
      >
        <Stepper
          v-if="!isCurrentSectionExcludedFromStepper"
          :view="view"
          :hide-section-name="
            view.currentSection.hideStepperSectionName === true
          "
        />
        <Logo
          v-else-if="view.isLogoVisible"
          class="onboarding-questionnaire__logo"
          :withText="true"
        />
      </div>
    </Transition>

    <Transition name="fade" mode="out-in">
      <component
        :is="currentPageComponent"
        :key="view.currentPage.id"
        :view="view"
      />
    </Transition>

    <Transition name="button-fade" mode="out-in">
      <div
        v-if="view.isContinueButtonVisible"
        class="onboarding-questionnaire__button-wrapper"
      >
        <AppButton
          class="onboarding-questionnaire__button"
          :disabled="view.isLoadingNextPage"
          :loading="view.isLoadingNextPage"
          @click="view.continue()"
          >Continue</AppButton
        >
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import type { Raw } from 'vue'

import { QuestionnaireView } from '@/models/onboarding'
import { onboardingPages } from '@/models/onboarding.pages'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import type { AuthUser } from '@/models/interfaces.auth'
import { StorageKeys } from '@/helpers/storage.keys'

import Logo from '@/components/ui/Logo.vue'
import AppButton from '@/app/components/button/AppButton.vue'
import Stepper from '@/app/components/onboarding/Stepper.vue'
import Text from '@/app/components/onboarding/questionnaire/Text.vue'
import DynamicOptions from '@/app/components/onboarding/questionnaire/DynamicOptions.vue'
import Options from '@/app/components/onboarding/questionnaire/Options.vue'
import FinishingSetup from '@/app/components/onboarding/questionnaire/FinishingSetup.vue'
import Review from '@/app/components/onboarding/questionnaire/Review.vue'
import Statements from '@/app/components/onboarding/questionnaire/Statements.vue'
import Books from '@/app/components/onboarding/questionnaire/Books.vue'
import PersonalizedBooks from '@/app/components/onboarding/questionnaire/PersonalizedBooks.vue'
import DailyGoal from '@/app/components/onboarding/questionnaire/DailyGoal.vue'
import Notifications from '@/app/components/onboarding/questionnaire/Notifications.vue'
import CommitmentPact from '@/app/components/onboarding/questionnaire/CommitmentPact.vue'
import Welcome from '@/app/components/onboarding/questionnaire/Welcome.vue'
import Splitscreen from '@/app/components/onboarding/questionnaire/Splitscreen.vue'
import Upgrade from '@/app/components/onboarding/questionnaire/Upgrade.vue'
import GenreSelection from '@/app/components/onboarding/questionnaire/GenreSelection.vue'
import BookSelection from '@/app/components/onboarding/questionnaire/BookSelection.vue'
import Loading from '@/app/components/onboarding/questionnaire/Loading.vue'
import Summary from '@/app/components/onboarding/questionnaire/Summary.vue'
import MillionUsers from '@/app/components/onboarding/questionnaire/MillionUsers.vue'
import ProductDemo from '@/app/components/onboarding/questionnaire/ProductDemo.vue'
import LearningSetupSummary from '@/app/components/onboarding/questionnaire/LearningSetupSummary.vue'
import PlanSelection20676 from '@/app/components/onboarding/questionnaire/PlanSelection20676.vue'
import PaymentCapture20676 from '@/app/components/onboarding/questionnaire/PaymentCapture20676.vue'

const props = defineProps<{ nextOnboardingPage: () => void }>()

const pageComponents = {
  Text,
  Options,
  DynamicOptions,
  LearningSetupSummary,
  FinishingSetup,
  Review,
  Statements,
  Books,
  PersonalizedBooks,
  DailyGoal,
  Notifications,
  CommitmentPact,
  Welcome,
  Splitscreen,
  Upgrade,
  GenreSelection,
  BookSelection,
  Loading,
  Summary,
  MillionUsers,
  ProductDemo,
  PlanSelection20676,
  PaymentCapture20676,
}

const currentPageComponent = computed(() => {
  return pageComponents[view.value.currentPage.type]
})

const user = JSON.parse(
  localStorage.getItem(StorageKeys.user) || '{}',
) as AuthUser
const firstName = user.full_name?.match(/^[a-zA-ZÀ-ÿ]+/)?.[0]

const view = ref<Raw<QuestionnaireView>>(
  new QuestionnaireView(
    onboardingPages.questionnaire,
    props.nextOnboardingPage,
    firstName,
  ),
)

const isQuestionnaireHeaderVisible = computed(
  () => !view.value.currentPage.hideHeader,
)

const isSummaryPage = computed(
  () => view.value.currentPage.type === 'LearningSetupSummary',
)

const isPersonalizePage = computed(
  () => view.value.currentPage.id === 'personalize',
)

const isCurrentSectionExcludedFromStepper = computed(
  () => view.value.currentSection.excludeFromStepper === true,
)

const showStepperGradient = computed(
  () =>
    isQuestionnaireHeaderVisible.value &&
    (!isCurrentSectionExcludedFromStepper.value || isPersonalizePage.value),
)

onMounted(async () => {
  // Preload cache data while the user goes through the questionnaire.
  // We run it without `await` on purpose so that it doesn't block the
  // loading of the rest of the page.
  preloadCacheData()
})

async function preloadCacheData(): Promise<void> {
  try {
    await Promise.all([
      backend.getBooks(),
      backend.getArticles(),
      backend.getPodcasts(),
      backend.getBookCollections(),
      backend.getRecommendations(),
      backend.getExcerpt(),
      backend.getBookOfTheDay(),
      backend.getUserDocs(),
      backend.getBookTags(),
    ])
  } catch (error: any) {
    Sentry.captureException(error)
  }
}
</script>

<style lang="scss" scoped>
:global(#app) {
  overflow: auto;
}

.onboarding-questionnaire {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 100%;
  height: 100%;
  overflow: visible;
  isolation: isolate;
  @media (min-width: $w-medium-b) and (min-height: 900px) {
    --onboarding-padding: 48px;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 0;
    z-index: 99;
  }

  &__logo {
    display: block;
    height: 24px;
    margin-top: 22px;
    animation: fadeIn 1s forwards;
    @supports (padding-top: env(safe-area-inset-top)) {
      margin-top: max(22px, env(safe-area-inset-top));
    }
  }

  &__button-wrapper {
    position: relative;
    display: flex;
    justify-content: center;
    width: 100%;
    padding: var(--onboarding-padding);
    padding-top: 0;

    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      z-index: 0;
      display: block;
      width: 100%;
      height: calc(100% + var(--onboarding-padding));
      background: $c-bg;
      mask: linear-gradient(
        0deg,
        rgba(0, 0, 0, 1) 0%,
        rgba(0, 0, 0, 1) 65%,
        rgba(0, 0, 0, 0) 100%
      );
    }
  }

  &__button {
    z-index: 1;
    width: 100%;
    margin-top: calc(var(--onboarding-padding) * -1);
    max-width: var(--onboarding-inner-width);
    height: 56px;
    font-size: 18px;
  }

  &__header-gradient {
    position: absolute;
    top: 0;
    left: 50%;
    z-index: -1;
    width: 100%;
    max-width: 1136px;
    pointer-events: none;
    transform: translateX(-50%);

    @media (min-width: $w-pc-b) {
      top: -32px;
    }
  }

  &.onboarding-questionnaire--personalize {
    :deep(.onboarding-questionnaire__header-gradient) {
      z-index: 1;
    }
  }

  &__header-gradient-image {
    display: block;
    width: 100%;
    height: auto;
  }

  :deep(.onboarding-questionnaire__button) {
    background-color: $c-blue-4;
    color: $c-white;

    @include hover {
      background-color: darken($c-blue-4, 8%);
      color: $c-white;
    }
  }
}

.onboarding-questionnaire--default {
  .onboarding-questionnaire__header {
    min-height: 46px;
  }

  &.onboarding-questionnaire--summary {
    .onboarding-questionnaire__button-wrapper {
      &::after {
        background: $c-white;

        @media only screen and (min-width: $w-small) {
          background: $c-bg;
        }
      }

      .onboarding-questionnaire__button {
        box-shadow:
          0 -14px 28px -6px rgb(255 255 255 / 0.85),
          0 -4px 12px rgb(255 255 255 / 0.55);
      }
    }
  }

  :deep(h1 span:not([class])),
  :deep(h2 span:not([class])) {
    background: transparent;
    padding-inline: 0;
    padding-block: 0;
    color: #5aa96b;
  }
}

.fade {
  &-enter-active,
  &-leave-active {
    // Prevents clicking buttons during the transition.
    pointer-events: none;
  }
}

.header-gradient {
  &-enter-active,
  &-leave-active {
    pointer-events: none;
  }

  &-enter-active {
    transition:
      opacity 1.9s cubic-bezier(0.22, 1, 0.36, 1),
      transform 1.9s cubic-bezier(0.22, 1, 0.36, 1);
  }

  &-leave-active {
    transition:
      opacity 1.1s ease-in,
      transform 1.1s ease-in;
  }

  &-enter-from {
    opacity: 0;
    transform: translateX(-50%) translateY(-12px);
  }

  &-enter-to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }

  &-leave-from {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }

  &-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(-8px);
  }
}

.button-fade {
  &-enter-active,
  &-leave-active {
    pointer-events: none;
    transition: all 0.3s ease;
  }

  &-enter-active {
    // Prevents layout shifts from the button component disappearing.
    position: absolute;
    left: 0;
    bottom: 0;
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;
  }
}
</style>
